// @flow
/* eslint-disable */
import { option } from 'fp-ts';

import { ConfigurableAttribute, ConfigurableAttributes } from '../flowtypes/product';
import { getPath, get } from '../helpers/objects';
import { match } from '../helpers/adt';
import { prop, safeConcat } from '../helpers/functions';
import { formatPrice } from '../helpers/currency';
import {
  Attribute,
  getAttributeCode,
  extractAttribute,
  attributeIsCustomOption,
  formatAttributeType
} from '../types/attributes';
import type { BundleConfiguration } from '../flowtypes/bundles';
import { getBundleSelection, isBundleVirtual } from '../store/modules/bundles';

// Titles of Custom Options that we want to ignore.
const IGNORED_CUSTOM_OPTIONS = ['design'];

/* Product ADT */
export type ProductEntity = <T>(_: ProductEntityMatcher<T>) => T

type ProductEntityMatcher<T> = {
  Empty: (_: ?null) => T,
  Simple: (_: SimpleProductType) => T,
  Giftcard: (_: GiftcardDataType) => T,
  Configurable: (_: ConfigurableDataType) => T,
  Bundle: (_: BundleProductType) => T
}

export type GenericProductType = {
  id: number,
  sku: string,
  name: string,
  image: string,
  thumbnail: string,
  hasOptions: boolean,
  description: string,
  price: number,
  product_details: string,
  product_shipping_info: string,
  addToCartLabel: string,
  enterEditor: boolean,
  is_mobile_friendly: string,
}

type GiftcardAmount = {
  value_id: number,
  website_id: 0 | 1,
  value: number,
  row_id: number,
  attribute_id: number,
  website_value: number
}

type GiftcardFieldData = {
  is_configured: boolean,
  is_message_available: boolean,
  is_email_available: boolean,
  message_max_length: number,
  delivery_option: "print" | "email"
}

export type ConfigurableDataType = {
  // Common Props
  id: number,
  sku: string,
  name: string,
  image: string,
  thumbnail: string,
  hasOptions: boolean,
  description: string,
  price: number,
  product_details: string,
  product_shipping_info: string,
  addToCartLabel: string,
  enterEditor: boolean,
  is_mobile_friendly: string,

  // ConfigurableDataType Props
  type_id: 'configurable',
  configurable_attributes: ConfigurableAttributes,
  custom_options?: Array<Object>,
  livePreviewAttributes: Array<string>,
}

export type GiftcardDataType = {
  // Common Props
  id: number,
  sku: string,
  name: string,
  image: string,
  thumbnail: string,
  hasOptions: boolean,
  description: string,
  price: number,
  product_details: string,
  product_shipping_info: string,
  addToCartLabel: string,
  enterEditor: boolean,
  is_mobile_friendly: string,

  // GiftcardDataType Props
  type_id: 'giftcard',
  giftcard_amounts: Array<GiftcardAmount>,
  giftcard_data: GiftcardFieldData
}

export type SimpleProductType = {
  // Common Props
  id: number,
  sku: string,
  name: string,
  image: string,
  thumbnail: string,
  hasOptions: boolean,
  description: string,
  price: number,
  product_details: string,
  product_shipping_info: string,
  addToCartLabel: string,
  enterEditor: boolean,
  is_mobile_friendly: string,

  // SimpleProductType Props
  type_id: 'simple',
  custom_options?: Array<Object>,
}

export type BundleProductType = {
  id: number,
  sku: string,
  name: string,
  image: string,
  thumbnail: string,
  hasOptions: boolean,
  description: string,
  price: number,
  product_details: string,
  product_shipping_info: string,
  addToCartLabel: string,
  enterEditor: boolean,
  is_mobile_friendly: string,

  // SimpleProductType Props
  type_id: 'bundle',
  custom_options?: Array<Object>,
  bundle_options: BundleConfiguration;
}

export type ProductUnionType = ConfigurableDataType | GiftcardDataType | SimpleProductType;

// ProductUnionType :: ProductEntity
export function ProductAdt(data: ProductUnionType): ProductEntity {
  switch (data.type_id) {
    case 'simple':
      return Simple(data);
    case 'giftcard':
      return Giftcard(data);
    case 'configurable':
      return Configurable(data);
    case 'bundle':
      return Bundle(data);
    default: {
      return Empty(data);
    }
  }
}

const getProductPriceSimpleOrConfig = p => parseFloat(p.price);
// getProductPrice ::  ProductEntity => ?number
export const getProductPrice: (_: ProductEntity) =>?number = match({
  Giftcard: product => product.giftcard_amounts.sort((a, b) => a.value - b.value)[0].value,
  Simple: getProductPriceSimpleOrConfig,
  Configurable: getProductPriceSimpleOrConfig,
  Bundle: getProductPriceSimpleOrConfig,
  Empty: () => null,
});

const getBaseProductPriceSimpleOrConfig = p => parseFloat(p.lowest_price ? p.lowest_price : p.price);
export const getBaseProductPrice: (_: ProductEntity) =>?number = match({
  Giftcard: product => product.giftcard_amounts.sort((a, b) => a.value - b.value)[0].value,
  Simple: getBaseProductPriceSimpleOrConfig,
  Configurable: getBaseProductPriceSimpleOrConfig,
  Bundle: getBaseProductPriceSimpleOrConfig,
  Empty: () => null,
});

const getCatalogPriceRulesForProduct = p => p.catalog_price_rules ? p.catalog_price_rules : [];
export const getCatalogPriceRules: (_: ProductEntity) =>?number = match({
  Giftcard: product => [],
  Simple: getCatalogPriceRulesForProduct,
  Configurable: getCatalogPriceRulesForProduct,
  Bundle: getCatalogPriceRulesForProduct,
  Empty: () => null,
});

export const getProductId: (_: ProductEntity) =>?number = match({
  Giftcard: product => product.entity_id,
  Simple: product => product.entity_id,
  Configurable: product => product.entity_id,
  Bundle: product => product.entity_id,
  Empty: () => null,
});

// getDefaultGiftcardAttributes ::  ProductEntity => ConfigurableAttributes
const getDefaultGiftcardAttributes = (product: GiftcardDataType): ConfigurableAttributes => [{
  id: 'giftcard_amount',
  type: 'select',
  label: 'Choose Amount',
  alignment: 'left-half',
  options: product.giftcard_amounts.sort((a, b) => a.value - b.value).map((amount: GiftcardAmount) => ({ ...amount, value: formatPrice(2)(amount.value) })),
  required: true,
}, {
    id: 'giftcard_sender_name',
    type: 'text',
    label: 'Sender Name',
    alignment: 'left-half',
    ...(getPath(['giftcard_data', 'is_email_available'])(product) ? {} : { maxLength: 22 }),
    required: true,
  },
  {
    id: 'giftcard_recipient_name',
    type: 'text',
    label: 'Recipient Name',
    required: true,
    ...(getPath(['giftcard_data', 'is_email_available'])(product) ? {} : {maxLength: 22} ),
    alignment: 'left-half',
    value: '',
  }
];

// getGiftcardAttributes ::  ProductEntity => ConfigurableAttributes
const getGiftcardAttributes = (product: GiftcardDataType): ConfigurableAttributes => [
  ...getDefaultGiftcardAttributes(product),
  ...(
    getPath(['giftcard_data', 'delivery_option'])(product) === 'print' && getPath(['giftcard_data', 'is_email_available'])(product)
      ? [{
        id: 'giftcard_sender_email',
        type: 'email',
        label: 'Sender Email',
        required: true,
        alignment: 'left-half',
        value: '',
      }] : []
  ),
  ...(
    getPath(['giftcard_data', 'is_email_available'])(product)
      ? [
        {
          id: 'giftcard_recipient_email',
          type: 'email',
          label: 'Recipient Email',
          alignment: 'right-half',
          required: true,
          value: '',
        }] : []
  ), ...(
    getPath(['giftcard_data', 'is_message_available'])(product)
      ? [{
        id: 'giftcard_message',
        type: 'textarea',
        label: 'Personal Note',
        maxLength: getPath(['giftcard_data', 'message_max_length'])(product) || 255,
        value: '',
      }] : []
  ), ...(
    getPath(['giftcard_data', 'delivery_option'])(product) === 'email'
      ? [{
        id: 'giftcard_delivery_date',
        type: 'date',
        label: 'Date to Send',
        alignment: 'left-half',
        required: true,
        value: '',
      }] : []
  ),
];

// Filters out any ignored custom options.
export const filterCustomOptions = (cos: Array<Object>) => (
  cos.filter(({ title }) => !IGNORED_CUSTOM_OPTIONS.includes(title))
);

// getConfigurableAttributes :: ProductEntity => ConfigurableAttributes
export const getConfigurableAttributes: (_: ProductEntity) => ConfigurableAttributes = match({
  Giftcard: getGiftcardAttributes,

  Simple: product => (
    prop('custom_options')(product)
      .map(cos => cos.map(co => Attribute({
        ...co,
        value: '',
        ...(IGNORED_CUSTOM_OPTIONS.includes(co.title) ? { hidden: true, value: (co.options ? co.options : co.values)[0].option_type_id } : {}),
      })))
      .map(attrs => attrs.map(extractAttribute))
      .getOrElseValue([])
  ),

  Configurable: product => (
    safeConcat(
      prop('configurable_attributes')(product)
        .map(cas => cas.map(attr => Attribute({
          ...attr,
          value: '',
          required: true,
          type: formatAttributeType(attr),
        })))
    )(
      prop('custom_options')(product)
        .map(cos => cos.map(co => Attribute({
          ...co,
          value: '',
          ...(IGNORED_CUSTOM_OPTIONS.includes(co.title) ? { hidden: true, value: co.values[0].option_type_id} : {}),
        })))
    )
    .map(extractAttribute)
  ),
  Bundle: () => [],
  Empty: () => [],
});

const defaultAddToCartBody = formKey => product => ({
  cart_page: parseInt(get('cart_page')(product), 10) ? 1 : 0,
  product: get('entity_id')(product),
  qty: 1,
  custom_product_type: get('custom_product_type')(product),
  form_key: formKey,
  options: false,
});

const getProductSelection = attributes => (
  attributes.reduce((selectionstring, attr) => (
    `${selectionstring}&${getAttributeCode(Attribute(attr)).getOrElseValue('').toLowerCase().trim()}=${attr.options.filter(opt => opt.id === attr.value)[0].label.trim()}`
  ), '')
);

export const getAddToCartBody = (formKey: string) => match({
  Simple: product => state => ({
    ...defaultAddToCartBody(formKey)(product),
    qty: state.quantity.value,
    ...state.attributes.reduce(
      (attributes, currentAttribute) => (currentAttribute.value && currentAttribute.value !== '' ?
        {
          ...attributes,
          [`options[${currentAttribute.option_id}]`]: currentAttribute.value || currentAttribute.values[0].option_type_id,
        } :
        attributes),
      {},
    ),
  }),
  Giftcard: (product) => state => ({
    ...defaultAddToCartBody(formKey)(product),
    ...state.attributes.reduce(
      (attributes, currentAttribute) => ({
        ...attributes,
        [currentAttribute.id]: currentAttribute.value
      }), {}
    )
  }),
  Configurable: (product) => state => ({
    ...defaultAddToCartBody(formKey)(product),
    qty: state.quantity.value,
    ...state.attributes.reduce(
      (attributes, currentAttribute) => {
        const attr = Attribute(currentAttribute);

        if (attributeIsCustomOption(attr)) {
          return {
            ...attributes,
            [`options[${currentAttribute.option_id}]`]: currentAttribute.value || currentAttribute.values[0].option_type_id,
          }
        }

        return {
          ...attributes,
          [`super_attribute[${currentAttribute.id}]`]: currentAttribute.value,
        };
      },
      {},
    ),
    'product-selection': getProductSelection(state.attributes),
  }),
  Bundle: (product) => state => ({
    ...defaultAddToCartBody(formKey)(product),
    qty: state.quantity.value,
    ...getBundleSelection(state.rawProduct)
      .reduce((bundleOptions, currentSelection) => ({
        ...bundleOptions,
        [`bundle_option[${currentSelection.option_id}]`]: currentSelection.option_selections.reduce((a, c) => c)
      }), {}),
  }),
  Empty: (product) => state => ({
    ...defaultAddToCartBody(formKey)(product),
    qty: state.quantity.value,
  }),
});

// getProductProperty :: string :: ProductEntity :: ?mixed
export const getConfigurableProduct = match({
  Giftcard: () => option.none,
  Simple: () => option.none,
  Configurable: product => option.some(product),
  Bundle: () => option.none,
  Empty: () => option.none,
});

export const getAddToCartUrl = match({
  Simple: (product) => get('addToCartUrl')(product),
  Giftcard: (product) => get('addToCartUrl')(product),
  Configurable: (product) => get('addToCartUrl')(product),
  Bundle: (product) => get('addToCartUrl')(product),
  Empty: (product) => null,
});

// getProductProperty :: string :: ProductEntity :: ?mixed
export const getProductProperty = (prop: string) => match({
  Giftcard: product => getPath([prop])(product) || null,
  Simple: product => getPath([prop])(product) || null,
  Configurable: product => getPath([prop])(product) || null,
  Bundle: product => getPath([prop])(product) || null,
  Empty: product => getPath([prop])(product) || null,
});

// getProductProperty :: string :: ProductEntity :: ?mixed
export const getProductPropertyOr = (prop: string) => (or: any) => match({
  Giftcard: product => getPath([prop])(product) || or,
  Simple: product => getPath([prop])(product) || or,
  Configurable: product => getPath([prop])(product) || or,
  Bundle: product => getPath([prop])(product) || or,
  Empty: product => getPath([prop])(product) || or,
});

// isSimple :: ProductEntity => boolean
export const isSimple = match({
  Giftcard: () => false,
  Simple: () => true,
  Configurable: () => false,
  Bundle: () => false,
  Empty: () => false,
});

// isConfigurable :: ProductEntity => boolean
export const isConfigurable = match({
  Giftcard: () => false,
  Simple: () => false,
  Configurable: () => true,
  Bundle: () => false,
  Empty: () => false,
});

export const isBundle = match({
  Giftcard: () => false,
  Simple: () => false,
  Configurable: () => false,
  Bundle: () => true,
  Empty: () => false,
});

// isVirtual :: ProductEntity => boolean
export const isVirtual = match({
  Giftcard: () => false,
  Simple: () => false,
  Configurable: () => true,
  Bundle: (p) => isBundleVirtual(p),
  Empty: (p) => option.fromNullable(p.type_id).map((type) => type === 'virtual').getOrElseValue(false),
});

// isConfigurable :: ProductEntity => boolean
export const isGiftcard = match({
  Giftcard: () => true,
  Simple: () => false,
  Configurable: () => false,
  Bundle: () => false,
  Empty: () => false,
});

// isCard :: ProductEntity => boolean
export const isCard = match({
  Giftcard: () => false,
  Simple: (p) => isCardProduct(p),
  Configurable: (p) => isCardProduct(p),
  Bundle: (p) => false,
  Empty: () => false,
});

const isCardProduct = (p) => {
  const props = [
    { name: 'custom_product_type', check: v => v.toLowerCase() === 'card' },
    { name: 'attribute_set', check: v => v.toLowerCase().indexOf('card') !== -1 }
  ];
  return props
    .map(_prop => prop(_prop.name)(p)
      .chain(value => typeof value === 'string' ? option.some(value) : option.none)
      .map(value => _prop.check(value))
      .getOrElseValue(false)
    ).reduce((previous, current) => previous || current);
};

const CARD_PRICING_ATTR = 'card_quantity';

export const getIndependentPricingAttr = (selection: Array<Object>) => selection.reduce(
  (accum: Object, attr: ConfigurableAttribute) => {
    if (attr.code !== CARD_PRICING_ATTR) {
      return accum;
    }
    return attr.options.filter(opt => opt.id === attr.value)[0];
  }, {});

// Simple :: SimpleProductType => ProductEntity
export function Simple(data: SimpleProductType): ProductEntity {
  return <T>(matcher: ProductEntityMatcher<T>): T => matcher.Simple(data);
}

// Giftcard :: GiftcardDataType => ProductEntity
export function Giftcard(data: GiftcardDataType): ProductEntity {
  return <T>(matcher: ProductEntityMatcher<T>): T => matcher.Giftcard(data);
}

// Configurable :: ConfigurableDataType => ProductEntity
export function Configurable(data: ConfigurableDataType): ProductEntity {
  return <T>(matcher: ProductEntityMatcher<T>): T => matcher.Configurable(data);
}

// Bundle :: BundleDataType => ProductEntity
export function Bundle(data: BundleProductType): ProductEntity {
  return <T>(matcher: ProductEntityMatcher<T>): T => matcher.Bundle(data);
}

// Empty :: ?null => ProductEntity
export function Empty(data: ?null = null): ProductEntity {
  return <T>(matcher: ProductEntityMatcher<T>): T => matcher.Empty(data);
}



// WEBPACK FOOTER //
// ./src/types/product.js